import { Sort } from '@rewardops/api'

import { SortOptions } from '~/components/maple/filters/generic-sort-menu'
import { useTranslation } from '~/app/i18n/client'
import type { NavigationLink } from '~/context/resources-provider'

const isPromotionsLink = (link?: NavigationLink) =>
  Boolean(link?.to?.endsWith('/promotions') || link?.to === 'promotions')

/**
 * Removes the promotions nav link for pointsAtWork tenants when there are no
 * active ELP promotions. All other links (and all other tenants) pass through
 * unchanged. Centralizes the logic shared by the desktop, stacked and mobile
 * nav bars.
 */
export const filterNavLinks = (
  links: NavigationLink[] | undefined,
  theme: string | undefined,
  showPromotionsLink = true,
): NavigationLink[] | undefined => {
  if (theme !== 'pointsAtWork' || showPromotionsLink) return links

  return links?.filter((link) => !isPromotionsLink(link))
}

export type ExtendSort = Sort & {
  key: string
  message: string
}

export const getSortOptionByKey = (key: string): Sort | undefined => {
  const sortOption = getLocalisedSortOptions().find(
    (option) => option.key === key,
  )
  return sortOption as Sort
}

export enum SortOptionsKeys {
  CreatedAt = 'created_at',
  SalePriceAsc = 'member_sale_price_ascending',
  SalePriceDesc = 'member_sale_price_descending',
  NameAsc = 'name_ascending',
  NameDesc = 'name_descending',
  Featured = 'featured',
}

export const getLocalisedSortOptions = (): ExtendSort[] => {
  const { t } = useTranslation()

  return [
    {
      key: SortOptionsKeys.CreatedAt,
      by: 'CREATED_DATE',
      order: 'DESCENDING',
      message: t('filters.newestArrivals'),
    },
    {
      key: SortOptionsKeys.SalePriceAsc,
      by: 'MEMBER_SALE_PRICE',
      order: 'ASCENDING',
      message: t('filters.priceLowToHigh'),
    },
    {
      key: SortOptionsKeys.SalePriceDesc,
      by: 'MEMBER_SALE_PRICE',
      order: 'DESCENDING',
      message: t('filters.priceHighToLow'),
    },
    {
      key: SortOptionsKeys.NameAsc,
      by: 'NAME',
      order: 'ASCENDING',
      message: t('filters.nameAscending'),
    },
    {
      key: SortOptionsKeys.NameDesc,
      by: 'NAME',
      order: 'DESCENDING',
      message: t('filters.nameDescending'),
    },
  ]
}

export const getCustomLocalisedSortOptionsByKey = (
  key: string,
  sortOptions: SortOptions[],
): Sort | undefined => {
  const sortOption = getCustomLocalisedSortOptions(sortOptions).find(
    (option) => option.key === key,
  )
  return sortOption as Sort
}

export const getCustomLocalisedSortOptions = (
  sortOptions: SortOptions[],
): ExtendSort[] => {
  const { t } = useTranslation()

  return sortOptions.map((option) => ({
    key: `${option?.name?.toLowerCase()}_${option?.order?.toLowerCase()}`,
    by: option.name,
    order: option?.order,
    message: t(`filters.${[option?.messageName]}`),
  }))
}
